import { useTranslation } from "react-i18next";
import {
  Stage,
  TaskAwardResponse,
  TaskDetail,
  TaskList,
  TaskStatus,
  TaskStatusResponse,
} from "../tasks.types";
import { ComponentProps, useMemo, useState } from "react";
import { cn, formatNumber } from "@/shared/utils";
import { openLink, openTelegramLink } from "@telegram-apps/sdk-react";
import { appConfig } from "@/lib/site-config";
import {
  InfiniteData,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import { useFetch } from "@/lib/axios.config";
import { PaginationResponse } from "@/shared/types/api.types";
import showCustomToast from "@/shared/utils/showCustomToast";
import GetDoneSuccessIcon from "@/assets/icons/getdon_success.svg?react";
import { boom } from "@/lib/exposion";
import Spinner from "@/components/common/Spinner";
import { TaskKeys } from "../tasks.api";
import GreatResult from "../GreatResult";
import { clearTaskStatus, taskStatusStorageKey } from "./taskStatusStorage";

interface StageButtonProps {
  task: TaskList;
  currentStage: Stage | null;
  onClose?: () => void;
  isDone?: boolean;
}

const verifyTask = async (task: TaskList) => {
  const response = await useFetch.post<{
    state: TaskStatusResponse;
    cur_stage_value: number;
  }>(`/tasks/ClaimTaskAward/${task.id}`, {
    keyword: "",
  });
  return response.data;
};

// Same set App.tsx refreshes after an X follow: a task can be on screen through
// the list, a collection, or a single-task query, and a paid stage changes it in
// all of them.
const TASK_QUERY_KEYS = [
  TaskKeys.TaskList,
  TaskKeys.Collection,
  TaskKeys.TaskCollectoinDetail,
  TaskKeys.TaskDetail,
];

const handleOpenLink = (link: string) => {
  if (!link) return;
  if (link.startsWith(appConfig.telegram.baseUrl)) {
    return openTelegramLink(link);
  }

  openLink(link);
};

const StageButton = ({
  task,
  isDone,
}: StageButtonProps) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  // Share the key format with the other task buttons, so clearTaskStatus() can
  // actually reach this one. The old `taskStatus-<id>` key it used to write did
  // not match that helper's `taskStatus-<id>-` prefix.
  const storageKey = useMemo(() => taskStatusStorageKey(task.id), [task.id]);
  const [isPerformedAction, setIsPerformedAction] = useState(
    () => localStorage.getItem(storageKey) === "PERFORMED"
  );

  const performAction = () => {
    handleOpenLink(task.link);
    localStorage.setItem(storageKey, "PERFORMED");
    setIsPerformedAction(true);
  };

  // The check says the task is not done after all. Forget the cached step so the
  // button goes back to "perform" — otherwise it stays on "verify" for good and
  // the link that actually completes the task can never be opened again.
  const backToPerform = () => {
    clearTaskStatus(task.id);
    setIsPerformedAction(false);
  };

  // The stage card renders straight off the cached task: the header count and
  // the progress bar come from user_process.cur_stage_value, the gold rows from
  // stage.awarded. Both only ever change server-side, so write the count the
  // claim just reported into the cache — the list is served from a replica that
  // can still answer with the pre-award row, and then nothing would move the
  // card until the next cold open.
  const cacheStageProgress = (curStageValue: number, stagesPaid: boolean) => {
    const applyProgress = (cached: TaskList): TaskList => {
      if (cached.id !== task.id) return cached;
      return {
        ...cached,
        user_process: {
          status: cached.user_process?.status ?? TaskStatus.ReadyAward,
          completed_at: cached.user_process?.completed_at ?? "",
          user_message: cached.user_process?.user_message ?? "",
          cur_stage_value: curStageValue,
        },
        stages: cached.stages.map((stage) =>
          stagesPaid && stage.value <= curStageValue
            ? { ...stage, awarded: true }
            : stage
        ),
      };
    };

    queryClient.setQueriesData<InfiniteData<PaginationResponse<TaskList>>>(
      { queryKey: [TaskKeys.TaskList] },
      (cached) =>
        cached && {
          ...cached,
          pages: cached.pages.map((page) => ({
            ...page,
            results: page.results.map(applyProgress),
          })),
        }
    );

    // The card is also opened from a collection or a single-task page. Those
    // queries have a different shape from the infinite list, so invalidating
    // them alone can redraw the old replica response right after a claim.
    queryClient.setQueriesData<TaskList>(
      { queryKey: [TaskKeys.TaskDetail] },
      (cached) => (cached ? applyProgress(cached) : cached)
    );
    queryClient.setQueriesData<TaskDetail>(
      { queryKey: [TaskKeys.TaskCollectoinDetail] },
      (cached) =>
        cached
          ? { ...cached, tasks: cached.tasks.map(applyProgress) }
          : cached
    );
  };

  const refreshTaskProgress = () =>
    Promise.all([
      ...TASK_QUERY_KEYS.map((key) =>
        queryClient.invalidateQueries({ queryKey: [key] })
      ),
      // The stage award landed in the wallet the header reads from.
      queryClient.invalidateQueries({ queryKey: ["profile"] }),
    ]);

  const { mutateAsync: claimReward, isPending: isClaiming } = useMutation({
    mutationFn: () => verifyTask(task),
    onSuccess(data) {
      if (["INCOMPLETE", "RETRY_LATER"].includes(data.state)) {
        backToPerform();
        showCustomToast(t("incomplete_the_task"), "error");
      } else {
        // A multi-stage claim answers READY_AWARD, never COMPLETED, so keying
        // this on COMPLETED left the button stuck on its "performed" step.
        localStorage.removeItem(storageKey);
      }
    },
  });

  const { mutateAsync: getAward, isPending: isVerifying } = useMutation({
    mutationFn: async () => {
      const response = await useFetch.post<{
        state: TaskAwardResponse;
        award: string;
      }>(`/tasks/GetTaskAward/${task.id}`);
      return response.data;
    },
    onSuccess: (data) => {
      if (data.state === "COMPLETED" && parseFloat(data.award) > 0) {
        showCustomToast(t("congratulations"), "success", {
          description: t("get_done_successfully", {
            don: formatNumber(data.award),
          }),
          icon: <GetDoneSuccessIcon className="size-7" />,
        });
        boom();
      }
    },
  });

  const getAwardHandler = async () => {
    // A failed request left the button silent before, so the tap read as dead.
    const res = await claimReward().catch(() => null);
    if (!res) {
      showCustomToast(t("error_occurred"), "error");
      return;
    }

    // claimReward already reported these and sent the button back a step.
    if (["INCOMPLETE", "RETRY_LATER"].includes(res.state)) return;

    const new_cur_stage = res.cur_stage_value;

    let awarded = false;

    for (let i = 0; i < task.stages.length; i++) {
      if (task.stages[i].value <= new_cur_stage && !task.stages[i].awarded) {
        if (res.state === "COMPLETED" || res.state === "READY_AWARD") {
          // Read the answer, don't assume the attempt paid. A stage the cache
          // still shows as unpaid can already be settled server-side, and then
          // the award call comes back COMPLETED with award 0 (or NOT_READY) —
          // counting that as a payout skipped the step back below and left the
          // button on "verify" with nothing credited.
          const award = await getAward().catch(() => null);
          awarded =
            award?.state === "COMPLETED" && parseFloat(award.award) > 0;
          break;
        }
      }
    }

    if (!awarded) {
      // Nothing was claimable: the reached stage is already paid and the next
      // one still wants more (5 of the 6 it needs). The claim answers
      // READY_AWARD in that case, so without this the button kept its
      // "verify" step forever and every tap paid out zero.
      backToPerform();
      showCustomToast(t("incomplete_the_task"), "error");
    }

    // Even when no new stage was paid the count itself moved (17 recordings out
    // of the 50 the next stage wants), so the bar has to follow it.
    if (typeof new_cur_stage === "number") {
      cacheStageProgress(new_cur_stage, awarded);
    }
    await refreshTaskProgress();
  };

  const isPending = isClaiming || isVerifying;

  if (isDone) {
    return <GreatResult taskAwards={task.award} />;
  }

  if (isPerformedAction) {
    return (
      <BaseButton
        type="button"
        isLoading={isPending}
        className="bg-white text-black"
        onClick={getAwardHandler}
      >
        {t("task_status.PERFORMED")}
      </BaseButton>
    );
  }

  return (
    <BaseButton
      type="button"
      className="bg-yellow-linear text-dark"
      onClick={performAction}
    >
      {t("task_status.INCOMPLETE")}
    </BaseButton>
  );
};

export default StageButton;

const BaseButton = ({
  className,
  isLoading,
  children,
  disabled,
  ...props
}: ComponentProps<"button"> & { isLoading?: boolean }) => {
  return (
    <button
      type="button"
      className={cn(
        "h-11 flex-center disabled:bg-white/08 disabled:pointer-events-none gap-1 w-full rounded-xl text-sm font-semibold",
        className
      )}
      disabled={disabled || isLoading}
      {...props}
    >
      {isLoading ? <Spinner /> : children}
    </button>
  );
};
